import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    Icon,
    Link as MuiLink,
    MenuItem,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography
} from "@mui/material";
import {useDialog} from "muibox";
import {useState} from "react";
import {Link} from "react-router-dom";
import {
    isAnoMesValido,
    useCarregarMockContasBancarias,
    useCarregarMockGestaoFiscal,
    usePrepararContasBancarias,
    usePrepararGestaoFiscal
} from "../../api/DpgeIntegracaoEsfingeApi";
import useFeedback from "../../components/feedback/useFeedback";
import {useLoading} from "../../components/loading/LoadingProvider";
import Page from "../../components/layout/page/Page";
import PageHeader from "../../components/page-header/PageHeader";
import Voltar from "../../components/voltar/Voltar";
import {HABILITA_MASSA_MOCKADA} from "../../shared/env";
import {getLabelEstado} from "../../shared/estados";
import {
    dataPublicacaoRgfValida,
    descricaoAnexosRgfDpge,
    encerraQuadrimestreDpge,
    janelaPublicacaoRgfDpge,
    TIPO_MEIO_DIARIO_OFICIAL_ESTADO,
    TIPO_RELATORIO_RGF
} from "../../shared/gestaoFiscalDpge";

const TAB_CONTAS = "contas";
const TAB_FISCAL = "fiscal";

function IntegracaoFinanceiraPage() {
    const [aba, setAba] = useState(TAB_CONTAS);
    const [contas, setContas] = useState(estadoInicialContas);
    const [fiscal, setFiscal] = useState(estadoInicialFiscal);
    const [resultado, setResultado] = useState(null);
    const prepararContas = usePrepararContasBancarias();
    const prepararFiscal = usePrepararGestaoFiscal();
    const carregarMockContas = useCarregarMockContasBancarias();
    const carregarMockFiscal = useCarregarMockGestaoFiscal();
    const dialog = useDialog();
    const setLoading = useLoading();
    const {showError} = useFeedback();

    async function executar(titulo, mutation, payload) {
        try {
            await dialog.confirm({
                title: titulo,
                description:
                    "Os dados serão preparados para revisão no estado AGUARDANDO APROVAÇÃO. A transmissão exige autorização posterior no detalhe da ocorrência.",
                ok: {text: "PREPARAR", color: "primary"},
                cancel: {text: "CANCELAR", color: "primary"}
            });
        } catch (error) {
            return;
        }

        setLoading(true);
        try {
            const response = await mutation.mutateAsync(payload);
            setResultado({titulo, data: response.data});
        } catch (error) {
            showError(error);
        } finally {
            setLoading(false);
        }
    }

    function handlePrepararContas() {
        if (!contasBancariasValidas(contas)) {
            showError(
                "Revise a competência e os campos obrigatórios das contas ou encerramentos."
            );
            return;
        }
        executar(
            "Preparar contas bancárias para validação?",
            prepararContas,
            payloadContasBancarias(contas)
        );
    }

    function handlePrepararFiscal() {
        if (!gestaoFiscalValida(fiscal)) {
            showError(
                "Revise o período, os componentes fiscais e as publicações obrigatórias."
            );
            return;
        }
        executar(
            "Preparar gestão fiscal para validação?",
            prepararFiscal,
            payloadGestaoFiscal(fiscal)
        );
    }

    async function handleCarregarMockContas() {
        if (!isAnoMesValido(contas.anoMes)) {
            showError(
                "Informe uma competência válida antes de carregar a massa."
            );
            return;
        }

        setLoading(true);
        try {
            const response = await carregarMockContas.mutateAsync(
                contas.anoMes
            );
            setContas(response.data);
            setResultado(null);
        } catch (error) {
            showError(error);
        } finally {
            setLoading(false);
        }
    }

    async function handleCarregarMockFiscal() {
        if (
            !/^\d{4}$/.test(fiscal.ano) ||
            !/^[1-6]$/.test(`${fiscal.bimestre}`)
        ) {
            showError(
                "Informe um ano e um bimestre válidos antes de carregar a massa."
            );
            return;
        }

        setLoading(true);
        try {
            const response = await carregarMockFiscal.mutateAsync({
                ano: fiscal.ano,
                bimestre: Number(fiscal.bimestre)
            });
            setFiscal({
                ...response.data,
                bimestre: `${response.data.bimestre}`
            });
            setResultado(null);
        } catch (error) {
            showError(error);
        } finally {
            setLoading(false);
        }
    }

    return (
        <Page maxWidth="lg">
            <PageHeader
                primary="Integração Financeira"
                secondary="Preparação assistida de Contas Bancárias e Gestão Fiscal."
                commands={[
                    <Box key="voltar" sx={{display: {xs: "none", sm: "block"}}}>
                        <Voltar />
                    </Box>
                ]}
            />

            <Alert severity="warning" sx={{mb: 2}}>
                A preparação gera ocorrências em{" "}
                <strong>AGUARDANDO APROVAÇÃO</strong>. Confira os dados, o
                ambiente e o modo de envio antes de solicitar a autorização no
                detalhe da ocorrência. A preparação não transmite dados.
            </Alert>

            <Paper
                variant="outlined"
                sx={{borderRadius: 3, minWidth: 0, overflow: "hidden"}}
            >
                <Tabs
                    value={aba}
                    onChange={(event, value) => {
                        setAba(value);
                        setResultado(null);
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab
                        value={TAB_CONTAS}
                        icon={<Icon>account_balance</Icon>}
                        iconPosition="start"
                        label="Contas bancárias"
                    />
                    <Tab
                        value={TAB_FISCAL}
                        icon={<Icon>assessment</Icon>}
                        iconPosition="start"
                        label="Gestão fiscal"
                    />
                </Tabs>
                <Divider />

                <Box sx={{minWidth: 0, p: {xs: 2, md: 3}}}>
                    {aba === TAB_CONTAS && (
                        <ContasBancariasForm
                            value={contas}
                            onChange={setContas}
                            onPreparar={handlePrepararContas}
                            onCarregarMock={handleCarregarMockContas}
                            preparando={prepararContas.isLoading}
                            carregandoMock={carregarMockContas.isLoading}
                        />
                    )}
                    {aba === TAB_FISCAL && (
                        <GestaoFiscalForm
                            value={fiscal}
                            onChange={setFiscal}
                            onPreparar={handlePrepararFiscal}
                            onCarregarMock={handleCarregarMockFiscal}
                            preparando={prepararFiscal.isLoading}
                            carregandoMock={carregarMockFiscal.isLoading}
                        />
                    )}
                    <ResultadoPreparacao resultado={resultado} />
                </Box>
            </Paper>
        </Page>
    );
}

function ContasBancariasForm({
    value,
    onChange,
    onPreparar,
    onCarregarMock,
    preparando,
    carregandoMock
}) {
    function atualizarCompetencia(event) {
        onChange({
            ...value,
            anoMes: somenteDigitos(event.target.value).slice(0, 6)
        });
    }

    function atualizarConta(index, campo, novoValor) {
        onChange({
            ...value,
            contasBancarias: atualizarItem(
                value.contasBancarias,
                index,
                campo,
                novoValor
            )
        });
    }

    function atualizarEncerramento(index, campo, novoValor) {
        onChange({
            ...value,
            encerramentoContasBancarias: atualizarItem(
                value.encerramentoContasBancarias,
                index,
                campo,
                novoValor
            )
        });
    }

    return (
        <Stack spacing={3}>
            <SectionTitle
                icon="account_balance"
                title="Cadastro e encerramento de contas"
                description="Informe as movimentações da competência. As quantidades do leiaute serão calculadas pelo conector."
            />

            <Alert severity="info">
                Antes da autorização, será obrigatório conferir no TCE Virtual a
                remessa de Registros Contábeis da competência anterior para a
                mesma unidade gestora e registrar uma referência verificável. A
                preparação não cria nem substitui essa remessa contábil.
            </Alert>

            <MockDataAction onClick={onCarregarMock} loading={carregandoMock} />

            <TextField
                label="Competência *"
                value={value.anoMes}
                onChange={atualizarCompetencia}
                error={
                    value.anoMes.length === 6 && !isAnoMesValido(value.anoMes)
                }
                helperText="Formato AAAAMM"
                inputProps={{maxLength: 6}}
                sx={{maxWidth: 220}}
            />

            <ListaSection
                title="Contas bancárias"
                description="Contas ativadas ou incluídas no cadastro institucional."
                addLabel="Adicionar conta"
                onAdd={() =>
                    onChange({
                        ...value,
                        contasBancarias: [
                            ...value.contasBancarias,
                            novaContaBancaria()
                        ]
                    })
                }
            >
                {value.contasBancarias.length === 0 && (
                    <EmptyState text="Nenhuma conta incluída nesta competência." />
                )}
                {value.contasBancarias.map((conta, index) => (
                    <ContaBancariaCard
                        key={index}
                        index={index}
                        value={conta}
                        onChange={atualizarConta}
                        onRemove={() =>
                            onChange({
                                ...value,
                                contasBancarias: removerItem(
                                    value.contasBancarias,
                                    index
                                )
                            })
                        }
                    />
                ))}
            </ListaSection>

            <ListaSection
                title="Encerramentos"
                description="Contas encerradas dentro da competência informada."
                addLabel="Adicionar encerramento"
                onAdd={() =>
                    onChange({
                        ...value,
                        encerramentoContasBancarias: [
                            ...value.encerramentoContasBancarias,
                            novoEncerramento()
                        ]
                    })
                }
            >
                {value.encerramentoContasBancarias.length === 0 && (
                    <EmptyState text="Nenhum encerramento incluído nesta competência." />
                )}
                {value.encerramentoContasBancarias.map(
                    (encerramento, index) => (
                        <EncerramentoCard
                            key={index}
                            index={index}
                            value={encerramento}
                            onChange={atualizarEncerramento}
                            onRemove={() =>
                                onChange({
                                    ...value,
                                    encerramentoContasBancarias: removerItem(
                                        value.encerramentoContasBancarias,
                                        index
                                    )
                                })
                            }
                        />
                    )
                )}
            </ListaSection>

            <ActionBar
                label="Preparar contas para validação"
                icon="fact_check"
                disabled={!contasBancariasValidas(value) || preparando}
                onClick={onPreparar}
            />
        </Stack>
    );
}

function ContaBancariaCard({index, value, onChange, onRemove}) {
    return (
        <RegistroCard
            title={`Conta ${index + 1}`}
            subtitle={identificacaoConta(value)}
            onRemove={onRemove}
        >
            <Grid container spacing={2}>
                <Campo item xs={12} sm={3}>
                    <TextField
                        fullWidth
                        label="Banco *"
                        value={value.codigoBanco}
                        inputProps={{maxLength: 4}}
                        onChange={(event) =>
                            onChange(index, "codigoBanco", event.target.value)
                        }
                    />
                </Campo>
                <Campo item xs={8} sm={3}>
                    <TextField
                        fullWidth
                        label="Agência *"
                        value={value.codigoAgencia}
                        inputProps={{maxLength: 5}}
                        onChange={(event) =>
                            onChange(index, "codigoAgencia", event.target.value)
                        }
                    />
                </Campo>
                <Campo item xs={4} sm={2}>
                    <TextField
                        fullWidth
                        label="DV agência *"
                        value={value.digitoVerificadorAgenciaBancaria}
                        inputProps={{maxLength: 1}}
                        onChange={(event) =>
                            onChange(
                                index,
                                "digitoVerificadorAgenciaBancaria",
                                event.target.value
                            )
                        }
                    />
                </Campo>
                <Campo item xs={8} sm={3}>
                    <TextField
                        fullWidth
                        label="Conta *"
                        value={value.codigoConta}
                        inputProps={{maxLength: 14}}
                        onChange={(event) =>
                            onChange(index, "codigoConta", event.target.value)
                        }
                    />
                </Campo>
                <Campo item xs={4} sm={1}>
                    <TextField
                        fullWidth
                        label="DV *"
                        value={value.digitoVerificadorContaBancaria}
                        inputProps={{maxLength: 1}}
                        onChange={(event) =>
                            onChange(
                                index,
                                "digitoVerificadorContaBancaria",
                                event.target.value
                            )
                        }
                    />
                </Campo>
                <Campo item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Nome da conta *"
                        value={value.nomeConta}
                        inputProps={{maxLength: 255}}
                        onChange={(event) =>
                            onChange(index, "nomeConta", event.target.value)
                        }
                    />
                </Campo>
                <Campo item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        label="CNPJ do titular *"
                        value={value.numeroCNPJTitulaContaBancaria}
                        inputProps={{maxLength: 14}}
                        helperText="Somente 14 dígitos"
                        onChange={(event) =>
                            onChange(
                                index,
                                "numeroCNPJTitulaContaBancaria",
                                somenteDigitos(event.target.value).slice(0, 14)
                            )
                        }
                    />
                </Campo>
                <Campo item xs={6} sm={3}>
                    <TextField
                        fullWidth
                        label="Tipo da conta *"
                        value={value.tipoConta}
                        inputProps={{maxLength: 2}}
                        helperText="Tabela 118"
                        onChange={(event) =>
                            onChange(index, "tipoConta", event.target.value)
                        }
                    />
                </Campo>
                <Campo item xs={6} sm={4}>
                    <TextField
                        fullWidth
                        label="Cidade da agência (IBGE) *"
                        value={value.codigoIBGECidadeAgenciaBancaria}
                        inputProps={{maxLength: 7}}
                        onChange={(event) =>
                            onChange(
                                index,
                                "codigoIBGECidadeAgenciaBancaria",
                                somenteDigitos(event.target.value).slice(0, 7)
                            )
                        }
                    />
                </Campo>
                <Campo item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        type="date"
                        label="Data de ativação *"
                        value={value.dataAtivacaoContaBancaria}
                        InputLabelProps={{shrink: true}}
                        onChange={(event) =>
                            onChange(
                                index,
                                "dataAtivacaoContaBancaria",
                                event.target.value
                            )
                        }
                    />
                </Campo>
            </Grid>
        </RegistroCard>
    );
}

function EncerramentoCard({index, value, onChange, onRemove}) {
    return (
        <RegistroCard
            title={`Encerramento ${index + 1}`}
            subtitle={identificacaoConta(value)}
            onRemove={onRemove}
        >
            <Grid container spacing={2}>
                <Campo item xs={12} sm={3}>
                    <TextField
                        fullWidth
                        label="Banco *"
                        value={value.codigoBanco}
                        inputProps={{maxLength: 4}}
                        onChange={(event) =>
                            onChange(index, "codigoBanco", event.target.value)
                        }
                    />
                </Campo>
                <Campo item xs={8} sm={3}>
                    <TextField
                        fullWidth
                        label="Agência *"
                        value={value.codigoAgencia}
                        inputProps={{maxLength: 5}}
                        onChange={(event) =>
                            onChange(index, "codigoAgencia", event.target.value)
                        }
                    />
                </Campo>
                <Campo item xs={4} sm={2}>
                    <TextField
                        fullWidth
                        label="DV agência *"
                        value={value.digitoVerificadorAgenciaBancaria}
                        inputProps={{maxLength: 1}}
                        onChange={(event) =>
                            onChange(
                                index,
                                "digitoVerificadorAgenciaBancaria",
                                event.target.value
                            )
                        }
                    />
                </Campo>
                <Campo item xs={8} sm={3}>
                    <TextField
                        fullWidth
                        label="Conta *"
                        value={value.codigoConta}
                        inputProps={{maxLength: 14}}
                        onChange={(event) =>
                            onChange(index, "codigoConta", event.target.value)
                        }
                    />
                </Campo>
                <Campo item xs={4} sm={1}>
                    <TextField
                        fullWidth
                        label="DV *"
                        value={value.digitoVerificadorContaBancaria}
                        inputProps={{maxLength: 1}}
                        onChange={(event) =>
                            onChange(
                                index,
                                "digitoVerificadorContaBancaria",
                                event.target.value
                            )
                        }
                    />
                </Campo>
                <Campo item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        type="date"
                        label="Data de encerramento *"
                        value={value.dataEncerramentoContaBancaria}
                        InputLabelProps={{shrink: true}}
                        onChange={(event) =>
                            onChange(
                                index,
                                "dataEncerramentoContaBancaria",
                                event.target.value
                            )
                        }
                    />
                </Campo>
                <Campo item xs={12} sm={8}>
                    <TextField
                        fullWidth
                        label="Motivo do encerramento *"
                        value={value.descricaoMotivoEncerramentoContaBancaria}
                        inputProps={{maxLength: 256}}
                        onChange={(event) =>
                            onChange(
                                index,
                                "descricaoMotivoEncerramentoContaBancaria",
                                event.target.value
                            )
                        }
                    />
                </Campo>
            </Grid>
        </RegistroCard>
    );
}

function GestaoFiscalForm({
    value,
    onChange,
    onPreparar,
    onCarregarMock,
    preparando,
    carregandoMock
}) {
    function atualizarCampo(campo, novoValor) {
        onChange({...value, [campo]: novoValor});
    }

    function atualizarComponente(index, campo, novoValor) {
        atualizarCampo(
            "componentesFiscais",
            atualizarItem(value.componentesFiscais, index, campo, novoValor)
        );
    }

    function atualizarPublicidade(index, campo, novoValor) {
        atualizarCampo(
            "publicidades",
            atualizarItem(value.publicidades, index, campo, novoValor)
        );
    }

    function atualizarBimestre(novoBimestre) {
        const publicidades = encerraQuadrimestreDpge(novoBimestre)
            ? value.publicidades.length > 0
                ? value.publicidades.map((publicidade) => ({
                      ...publicidade,
                      tipoRelatorioFiscal: TIPO_RELATORIO_RGF
                  }))
                : [novaPublicidadeFiscal()]
            : [];
        onChange({...value, bimestre: novoBimestre, publicidades});
    }

    const encerraQuadrimestre = encerraQuadrimestreDpge(value.bimestre);
    const janelaPublicacao = janelaPublicacaoRgfDpge(value.ano, value.bimestre);

    return (
        <Stack spacing={3}>
            <SectionTitle
                icon="assessment"
                title="Relatório de Gestão Fiscal"
                description="Informe os componentes fiscais do bimestre e, no encerramento do quadrimestre, a publicação do RGF da DPGE/MS."
            />

            <Alert severity="warning">
                A obrigação de Gestão Fiscal permanece no planejamento da
                DPGE/MS. Esta tela permite preparar e revisar a massa, mas a
                autorização de envio está bloqueada até a validação formal do
                leiaute estadual com o TCE-MS.
            </Alert>

            <Alert severity="info">
                O manual e-Sfinge 2.0 publicado pelo TCE-MS relaciona códigos de
                componentes somente para Executivo e Legislativo municipais. Não
                reutilize esses códigos para a Defensoria. O RGF da DPGE/MS usa
                o tipo 02 e segue os anexos aplicáveis a outros Poderes e
                órgãos.
            </Alert>

            <MockDataAction onClick={onCarregarMock} loading={carregandoMock} />

            <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                    <TextField
                        fullWidth
                        label="Ano *"
                        value={value.ano}
                        inputProps={{maxLength: 4}}
                        onChange={(event) =>
                            atualizarCampo(
                                "ano",
                                somenteDigitos(event.target.value).slice(0, 4)
                            )
                        }
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        select
                        fullWidth
                        label="Bimestre *"
                        value={value.bimestre}
                        onChange={(event) =>
                            atualizarBimestre(event.target.value)
                        }
                    >
                        {[1, 2, 3, 4, 5, 6].map((bimestre) => (
                            <MenuItem key={bimestre} value={`${bimestre}`}>
                                {bimestre}º bimestre
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            </Grid>

            <ListaSection
                title="Componentes fiscais"
                description="Use somente códigos confirmados pelo TCE-MS para órgãos estaduais autônomos. A massa mockada não preenche códigos por aproximação."
                addLabel="Adicionar componente"
                onAdd={() =>
                    atualizarCampo("componentesFiscais", [
                        ...value.componentesFiscais,
                        novoComponenteFiscal()
                    ])
                }
            >
                {value.componentesFiscais.map((componente, index) => (
                    <RegistroCard
                        key={index}
                        title={`Componente ${index + 1}`}
                        subtitle={
                            componente.codigoComponente
                                ? `Código ${componente.codigoComponente}`
                                : "Código ainda não informado"
                        }
                        onRemove={() =>
                            atualizarCampo(
                                "componentesFiscais",
                                removerItem(value.componentesFiscais, index)
                            )
                        }
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Código do componente *"
                                    value={componente.codigoComponente}
                                    inputProps={{
                                        maxLength: 4,
                                        inputMode: "numeric"
                                    }}
                                    onChange={(event) =>
                                        atualizarComponente(
                                            index,
                                            "codigoComponente",
                                            somenteDigitos(
                                                event.target.value
                                            ).slice(0, 4)
                                        )
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    fullWidth
                                    label="Valor do componente *"
                                    value={componente.valorComponente}
                                    inputProps={{maxLength: 16}}
                                    helperText="Use ponto ou vírgula como separador decimal"
                                    onChange={(event) =>
                                        atualizarComponente(
                                            index,
                                            "valorComponente",
                                            event.target.value
                                        )
                                    }
                                />
                            </Grid>
                        </Grid>
                    </RegistroCard>
                ))}
            </ListaSection>

            {encerraQuadrimestre ? (
                <ListaSection
                    title="Publicações do RGF"
                    description={`O ${
                        value.bimestre
                    }º bimestre encerra quadrimestre. Para a DPGE/MS, confira ${descricaoAnexosRgfDpge(
                        value.bimestre
                    )}.`}
                    addLabel="Adicionar publicação"
                    onAdd={() =>
                        atualizarCampo("publicidades", [
                            ...value.publicidades,
                            novaPublicidadeFiscal()
                        ])
                    }
                >
                    {value.publicidades.map((publicidade, index) => {
                        const dataPreenchida = Boolean(
                            publicidade.dataPublicacao
                        );
                        const dataValida = dataPublicacaoRgfValida(
                            value.ano,
                            value.bimestre,
                            publicidade.dataPublicacao
                        );
                        const mensagemJanela = janelaPublicacao
                            ? `Período permitido: ${janelaPublicacao.dataMinima} a ${janelaPublicacao.dataMaxima}`
                            : "Informe um ano e um bimestre válidos.";

                        return (
                            <RegistroCard
                                key={index}
                                title={`Publicação ${index + 1}`}
                                subtitle={
                                    publicidade.dataPublicacao ||
                                    "Data ainda não informada"
                                }
                                onRemove={() =>
                                    atualizarCampo(
                                        "publicidades",
                                        removerItem(value.publicidades, index)
                                    )
                                }
                            >
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="Data da publicação *"
                                            value={publicidade.dataPublicacao}
                                            error={
                                                dataPreenchida && !dataValida
                                            }
                                            helperText={
                                                dataPreenchida && !dataValida
                                                    ? `Data fora da janela do RGF. ${mensagemJanela}`
                                                    : mensagemJanela
                                            }
                                            inputProps={{
                                                min: janelaPublicacao?.dataMinima,
                                                max: janelaPublicacao?.dataMaxima
                                            }}
                                            InputLabelProps={{shrink: true}}
                                            onChange={(event) =>
                                                atualizarPublicidade(
                                                    index,
                                                    "dataPublicacao",
                                                    event.target.value
                                                )
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="Tipo do meio *"
                                            value={
                                                publicidade.tipoMeioComunicacao
                                            }
                                            inputProps={{maxLength: 2}}
                                            helperText="Tabela 22"
                                            onChange={(event) =>
                                                atualizarPublicidade(
                                                    index,
                                                    "tipoMeioComunicacao",
                                                    event.target.value
                                                )
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <TextField
                                            fullWidth
                                            disabled
                                            label="Tipo do relatório"
                                            value={
                                                publicidade.tipoRelatorioFiscal
                                            }
                                            inputProps={{maxLength: 2}}
                                            helperText="02 — Relatório de Gestão Fiscal"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={5}>
                                        <TextField
                                            fullWidth
                                            label="Veículo de comunicação"
                                            value={
                                                publicidade.descricaoVeiculoComunicacao
                                            }
                                            inputProps={{maxLength: 100}}
                                            onChange={(event) =>
                                                atualizarPublicidade(
                                                    index,
                                                    "descricaoVeiculoComunicacao",
                                                    event.target.value
                                                )
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={7}>
                                        <TextField
                                            fullWidth
                                            label="URL da publicação *"
                                            value={publicidade.urlPublicidade}
                                            inputProps={{maxLength: 255}}
                                            onChange={(event) =>
                                                atualizarPublicidade(
                                                    index,
                                                    "urlPublicidade",
                                                    event.target.value
                                                )
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </RegistroCard>
                        );
                    })}
                </ListaSection>
            ) : (
                <Alert severity="info">
                    O {value.bimestre}º bimestre não encerra quadrimestre da
                    DPGE/MS. Este pacote não deve informar publicidade de RGF.
                </Alert>
            )}

            <ActionBar
                label="Preparar gestão fiscal para validação"
                icon="fact_check"
                disabled={!gestaoFiscalValida(value) || preparando}
                onClick={onPreparar}
            />
        </Stack>
    );
}

function SectionTitle({icon, title, description}) {
    return (
        <Stack
            direction={{xs: "column", sm: "row"}}
            spacing={1.5}
            alignItems={{xs: "flex-start", sm: "center"}}
        >
            <Box
                sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    borderRadius: 2,
                    width: 40,
                    height: 40,
                    display: "grid",
                    placeItems: "center"
                }}
            >
                <Icon>{icon}</Icon>
            </Box>
            <Box sx={{minWidth: 0}}>
                <Typography variant="h6" sx={{overflowWrap: "anywhere"}}>
                    {title}
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{overflowWrap: "anywhere"}}
                >
                    {description}
                </Typography>
            </Box>
        </Stack>
    );
}

function ListaSection({title, description, addLabel, onAdd, children}) {
    return (
        <Paper
            variant="outlined"
            sx={{minWidth: 0, p: {xs: 1.5, sm: 2}, borderRadius: 3}}
        >
            <Stack spacing={2}>
                <Stack
                    direction={{xs: "column", sm: "row"}}
                    justifyContent="space-between"
                    alignItems={{xs: "stretch", sm: "center"}}
                    spacing={1}
                >
                    <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                            {title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {description}
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<Icon>add</Icon>}
                        onClick={onAdd}
                        sx={{
                            alignSelf: {xs: "stretch", sm: "center"},
                            whiteSpace: "normal"
                        }}
                    >
                        {addLabel}
                    </Button>
                </Stack>
                {children}
            </Stack>
        </Paper>
    );
}

function RegistroCard({title, subtitle, onRemove, children}) {
    return (
        <Card
            variant="outlined"
            sx={{borderRadius: 2, minWidth: 0, overflow: "hidden"}}
        >
            <CardContent>
                <Stack spacing={2}>
                    <Stack
                        direction={{xs: "column", sm: "row"}}
                        justifyContent="space-between"
                        alignItems={{xs: "stretch", sm: "flex-start"}}
                        spacing={1}
                    >
                        <Box sx={{minWidth: 0}}>
                            <Typography variant="subtitle2">{title}</Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {subtitle}
                            </Typography>
                        </Box>
                        <Button
                            size="small"
                            color="error"
                            startIcon={<Icon>delete_outline</Icon>}
                            onClick={onRemove}
                            sx={{alignSelf: {xs: "flex-end", sm: "auto"}}}
                        >
                            Remover
                        </Button>
                    </Stack>
                    {children}
                </Stack>
            </CardContent>
        </Card>
    );
}

function MockDataAction({onClick, loading}) {
    if (!HABILITA_MASSA_MOCKADA) {
        return null;
    }

    return (
        <Alert
            severity="info"
            action={
                <Button
                    color="inherit"
                    size="small"
                    startIcon={<Icon>science</Icon>}
                    disabled={loading}
                    onClick={onClick}
                    sx={{whiteSpace: "nowrap"}}
                >
                    {loading ? "Carregando..." : "Carregar massa mockada"}
                </Button>
            }
            sx={{
                alignItems: "center",
                "& .MuiAlert-action": {
                    alignItems: "center",
                    ml: {xs: 0, sm: 2},
                    pt: {xs: 1, sm: 0}
                },
                flexDirection: {xs: "column", sm: "row"}
            }}
        >
            A massa contém apenas dados sintéticos e será carregada para
            conferência. Nenhuma ocorrência é criada nesta etapa.
        </Alert>
    );
}

function ActionBar({label, icon, disabled, onClick}) {
    return (
        <Stack direction="row" justifyContent="flex-end">
            <Button
                variant="contained"
                size="large"
                startIcon={<Icon>{icon}</Icon>}
                disabled={disabled}
                onClick={onClick}
                sx={{
                    width: {xs: "100%", sm: "auto"},
                    whiteSpace: "normal"
                }}
            >
                {label}
            </Button>
        </Stack>
    );
}

function ResultadoPreparacao({resultado}) {
    if (!resultado) {
        return null;
    }

    const data = resultado.data || {};
    const ocorrencias = data.ocorrencias || [];
    const metricas = [
        ["Contas", data.quantidadeContasBancarias],
        ["Encerramentos", data.quantidadeEncerramentos],
        ["Componentes", data.quantidadeComponentesFiscais],
        ["Publicações", data.quantidadePublicidades]
    ].filter((item) => item[1] !== undefined);

    return (
        <Paper variant="outlined" sx={{mt: 3, p: 2, borderRadius: 3}}>
            <Stack spacing={2}>
                <Typography variant="subtitle1">{resultado.titulo}</Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                    {metricas.map(([label, valor]) => (
                        <Chip
                            key={label}
                            variant="outlined"
                            label={`${label}: ${valor}`}
                        />
                    ))}
                </Stack>
                <Mensagens severity="success" mensagens={data.alertas} />
                <Mensagens severity="info" mensagens={data.melhorias} />
                {ocorrencias.length > 0 && (
                    <Stack spacing={1}>
                        <Typography variant="subtitle2">
                            Ocorrências preparadas
                        </Typography>
                        <Alert severity="info">
                            Revise os dados de cada ocorrência. A autorização,
                            quando disponível, acontece somente na tela de
                            detalhes e exige justificativa.
                        </Alert>
                        {ocorrencias.map((ocorrencia) => (
                            <MuiLink
                                key={ocorrencia.id}
                                component={Link}
                                to={`/eventos/${ocorrencia.id}`}
                                underline="hover"
                            >
                                Revisar ocorrência #{ocorrencia.id} —{" "}
                                {ocorrencia.eventoAtual} —{" "}
                                {getLabelEstado(ocorrencia.estadoEnvio)}
                            </MuiLink>
                        ))}
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
}

function Mensagens({severity, mensagens = []}) {
    if (!mensagens.length) {
        return null;
    }
    return (
        <Alert severity={severity}>
            <Box component="ul" sx={{m: 0, pl: 2}}>
                {mensagens.map((mensagem, index) => (
                    <li key={`${mensagem}-${index}`}>{mensagem}</li>
                ))}
            </Box>
        </Alert>
    );
}

function EmptyState({text}) {
    return (
        <Typography
            variant="body2"
            color="text.secondary"
            sx={{py: 1, textAlign: "center"}}
        >
            {text}
        </Typography>
    );
}

function Campo({children, ...props}) {
    return (
        <Grid item {...props}>
            {children}
        </Grid>
    );
}

export function payloadContasBancarias(value) {
    return {
        anoMes: value.anoMes,
        contasBancarias: value.contasBancarias.map(limparObjeto),
        encerramentoContasBancarias:
            value.encerramentoContasBancarias.map(limparObjeto)
    };
}

export function payloadGestaoFiscal(value) {
    return {
        ano: value.ano,
        bimestre: Number(value.bimestre),
        componentesFiscais: value.componentesFiscais.map(limparObjeto),
        publicidades: value.publicidades.map(limparObjeto)
    };
}

export function contasBancariasValidas(value) {
    if (!isAnoMesValido(value.anoMes)) {
        return false;
    }
    if (
        value.contasBancarias.length === 0 &&
        value.encerramentoContasBancarias.length === 0
    ) {
        return false;
    }
    return (
        value.contasBancarias.every(contaValida) &&
        value.encerramentoContasBancarias.every(encerramentoValido)
    );
}

export function gestaoFiscalValida(value) {
    const anoValido = /^\d{4}$/.test(value.ano);
    const bimestreValido = /^[1-6]$/.test(`${value.bimestre}`);
    const encerraQuadrimestre = encerraQuadrimestreDpge(value.bimestre);
    const publicidadesValidas = encerraQuadrimestre
        ? value.publicidades.length > 0 &&
          value.publicidades.every(
              (item) =>
                  item.dataPublicacao &&
                  dataPublicacaoRgfValida(
                      value.ano,
                      value.bimestre,
                      item.dataPublicacao
                  ) &&
                  /^\d{2}$/.test(item.tipoMeioComunicacao.trim()) &&
                  item.tipoRelatorioFiscal.trim() === TIPO_RELATORIO_RGF &&
                  item.urlPublicidade.trim()
          )
        : value.publicidades.length === 0;
    return (
        anoValido &&
        bimestreValido &&
        value.componentesFiscais.length > 0 &&
        value.componentesFiscais.every(
            (item) =>
                /^\d{4}$/.test(item.codigoComponente.trim()) &&
                item.valorComponente.trim().length > 0
        ) &&
        publicidadesValidas
    );
}

function contaValida(conta) {
    return (
        conta.codigoBanco.trim() &&
        conta.codigoAgencia.trim() &&
        conta.digitoVerificadorAgenciaBancaria.trim() &&
        conta.codigoConta.trim() &&
        conta.digitoVerificadorContaBancaria.trim() &&
        conta.nomeConta.trim() &&
        /^\d{14}$/.test(conta.numeroCNPJTitulaContaBancaria) &&
        conta.tipoConta.trim() &&
        /^\d{7}$/.test(conta.codigoIBGECidadeAgenciaBancaria) &&
        conta.dataAtivacaoContaBancaria
    );
}

function encerramentoValido(encerramento) {
    return (
        encerramento.codigoBanco.trim() &&
        encerramento.codigoAgencia.trim() &&
        encerramento.digitoVerificadorAgenciaBancaria.trim() &&
        encerramento.codigoConta.trim() &&
        encerramento.digitoVerificadorContaBancaria.trim() &&
        encerramento.dataEncerramentoContaBancaria &&
        encerramento.descricaoMotivoEncerramentoContaBancaria.trim()
    );
}

function limparObjeto(item) {
    return Object.fromEntries(
        Object.entries(item).map(([chave, valor]) => [
            chave,
            typeof valor === "string" ? valor.trim() : valor
        ])
    );
}

function atualizarItem(lista, index, campo, valor) {
    return lista.map((item, itemIndex) =>
        itemIndex === index ? {...item, [campo]: valor} : item
    );
}

function removerItem(lista, index) {
    return lista.filter((item, itemIndex) => itemIndex !== index);
}

function identificacaoConta(conta) {
    const partes = [
        conta.codigoBanco,
        conta.codigoAgencia,
        conta.codigoConta
    ].filter(Boolean);
    return partes.length ? partes.join(" / ") : "Dados ainda não informados";
}

function somenteDigitos(value) {
    return `${value || ""}`.replace(/\D/g, "");
}

function novaContaBancaria() {
    return {
        codigoBanco: "",
        codigoAgencia: "",
        digitoVerificadorAgenciaBancaria: "",
        codigoConta: "",
        digitoVerificadorContaBancaria: "",
        nomeConta: "",
        numeroCNPJTitulaContaBancaria: "",
        tipoConta: "",
        codigoIBGECidadeAgenciaBancaria: "",
        dataAtivacaoContaBancaria: ""
    };
}

function novoEncerramento() {
    return {
        codigoBanco: "",
        codigoAgencia: "",
        digitoVerificadorAgenciaBancaria: "",
        codigoConta: "",
        digitoVerificadorContaBancaria: "",
        dataEncerramentoContaBancaria: "",
        descricaoMotivoEncerramentoContaBancaria: ""
    };
}

function novoComponenteFiscal() {
    return {
        codigoComponente: "",
        valorComponente: ""
    };
}

function novaPublicidadeFiscal() {
    return {
        dataPublicacao: "",
        tipoMeioComunicacao: TIPO_MEIO_DIARIO_OFICIAL_ESTADO,
        tipoRelatorioFiscal: TIPO_RELATORIO_RGF,
        descricaoVeiculoComunicacao: "Diário Oficial do Estado",
        urlPublicidade: ""
    };
}

function competenciaAtual() {
    const hoje = new Date();
    return `${hoje.getFullYear()}${String(hoje.getMonth() + 1).padStart(
        2,
        "0"
    )}`;
}

function bimestreAtual() {
    return `${Math.ceil((new Date().getMonth() + 1) / 2)}`;
}

const estadoInicialContas = {
    anoMes: competenciaAtual(),
    contasBancarias: [novaContaBancaria()],
    encerramentoContasBancarias: []
};

const bimestreFiscalInicial = bimestreAtual();
const estadoInicialFiscal = {
    ano: `${new Date().getFullYear()}`,
    bimestre: bimestreFiscalInicial,
    componentesFiscais: [novoComponenteFiscal()],
    publicidades: encerraQuadrimestreDpge(bimestreFiscalInicial)
        ? [novaPublicidadeFiscal()]
        : []
};

export default IntegracaoFinanceiraPage;
